import { useState, useEffect } from 'react';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  zipCode: string;
}

interface NotificationPreferences {
  priceDrops: boolean;
  dailyDigest: boolean;
  weeklyDeals: boolean;
  newProducts: boolean;
  backInStock: boolean;
  priceThreshold: number;
}

interface ProfileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const defaultProfile: UserProfile = {
  id: '1',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  phone: '+1 (555) 123-4567',
  address: '123 Tech Street',
  city: 'San Francisco',
  country: 'United States',
  zipCode: '94105',
};

const defaultNotifications: NotificationPreferences = {
  priceDrops: true,
  dailyDigest: false,
  weeklyDeals: true,
  newProducts: false,
  backInStock: true,
  priceThreshold: 10,
};

const ProfileSidebar = ({ isOpen, onClose }: ProfileSidebarProps) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UserProfile>(defaultProfile);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeSection, setActiveSection] = useState<'profile' | 'notifications'>('profile');
  const [notifications, setNotifications] = useState<NotificationPreferences>(defaultNotifications);
  const [notificationsSaved, setNotificationsSaved] = useState(false);

  useEffect(() => {
    // Load profile from localStorage (simulating READ operation)
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      const parsed = JSON.parse(savedProfile);
      setProfile(parsed);
      setEditForm(parsed);
    } else {
      // Set default profile for demo
      setProfile(defaultProfile);
      setEditForm(defaultProfile);
      localStorage.setItem('userProfile', JSON.stringify(defaultProfile));
    }

    const savedNotifications = localStorage.getItem('notificationPreferences');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    }
  }, []);

  const handleNotificationToggle = (key: keyof NotificationPreferences) => {
    if (key === 'priceThreshold') return;
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleThresholdChange = (value: number) => {
    setNotifications(prev => ({ ...prev, priceThreshold: value }));
  };

  const saveNotifications = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    localStorage.setItem('notificationPreferences', JSON.stringify(notifications));
    setIsSubmitting(false);
    setNotificationsSaved(true);
    setTimeout(() => setNotificationsSaved(false), 3000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call (UPDATE operation)
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setProfile(editForm);
    localStorage.setItem('userProfile', JSON.stringify(editForm));
    setIsEditing(false);
    setIsSubmitting(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    
    // Simulate API call (DELETE operation)
    await new Promise(resolve => setTimeout(resolve, 800));
    
    localStorage.removeItem('userProfile');
    setProfile(null);
    setEditForm(defaultProfile);
    setShowDeleteConfirm(false);
    setIsSubmitting(false);
  };

  const handleCreateNew = async () => {
    setIsSubmitting(true);
    
    // Simulate API call (CREATE operation)
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newProfile = { ...editForm, id: Date.now().toString() };
    setProfile(newProfile);
    localStorage.setItem('userProfile', JSON.stringify(newProfile));
    setIsEditing(false);
    setIsSubmitting(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  if (!isOpen) return null;

  const renderNotificationsSection = () => (
    <div className="space-y-6">
      {notificationsSaved && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <i className="ri-check-line text-green-600 text-xl"></i>
          <span className="text-green-700 text-sm">Notification preferences saved!</span>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Email Notifications</h4>
        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-price-tag-3-line text-teal-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Price Drop Alerts</p>
                <p className="text-xs text-gray-500">Get notified when tracked prices drop</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleNotificationToggle('priceDrops')}
              className={`relative w-12 h-6 rounded-full transition-colors ${notifications.priceDrops ? 'bg-teal-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${notifications.priceDrops ? 'left-7' : 'left-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <i className="ri-mail-line text-amber-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Daily Digest</p>
                <p className="text-xs text-gray-500">Daily summary of price changes</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleNotificationToggle('dailyDigest')}
              className={`relative w-12 h-6 rounded-full transition-colors ${notifications.dailyDigest ? 'bg-teal-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${notifications.dailyDigest ? 'left-7' : 'left-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                <i className="ri-fire-line text-rose-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Weekly Deals</p>
                <p className="text-xs text-gray-500">Best deals of the week</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleNotificationToggle('weeklyDeals')}
              className={`relative w-12 h-6 rounded-full transition-colors ${notifications.weeklyDeals ? 'bg-teal-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${notifications.weeklyDeals ? 'left-7' : 'left-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <i className="ri-sparkling-line text-emerald-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">New Products</p>
                <p className="text-xs text-gray-500">Alerts for new product listings</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleNotificationToggle('newProducts')}
              className={`relative w-12 h-6 rounded-full transition-colors ${notifications.newProducts ? 'bg-teal-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${notifications.newProducts ? 'left-7' : 'left-1'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <i className="ri-inbox-archive-line text-indigo-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Back in Stock</p>
                <p className="text-xs text-gray-500">Notify when items are restocked</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleNotificationToggle('backInStock')}
              className={`relative w-12 h-6 rounded-full transition-colors ${notifications.backInStock ? 'bg-teal-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${notifications.backInStock ? 'left-7' : 'left-1'}`} />
            </button>
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Price Drop Threshold</h4>
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-3">Only notify me when prices drop by at least:</p>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="5"
              max="50"
              step="5"
              value={notifications.priceThreshold}
              onChange={(e) => handleThresholdChange(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
            />
            <span className="text-lg font-semibold text-teal-600 w-16 text-right">{notifications.priceThreshold}%</span>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span>5%</span>
            <span>50%</span>
          </div>
        </div>
      </div>

      <button
        onClick={saveNotifications}
        disabled={isSubmitting}
        className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>
            Saving...
          </span>
        ) : 'Save Preferences'}
      </button>
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transform transition-transform overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">My Profile</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Tab Navigation */}
        {profile && !isEditing && (
          <div className="px-6 pt-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveSection('profile')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeSection === 'profile' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveSection('notifications')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeSection === 'notifications' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Notifications
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <i className="ri-check-line text-green-600 text-xl"></i>
            <span className="text-green-700 text-sm">Profile updated successfully!</span>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {!profile && !isEditing ? (
            /* No Profile State */
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-add-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Profile Found</h3>
              <p className="text-gray-500 text-sm mb-6">Create your profile to get started</p>
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Create Profile
              </button>
            </div>
          ) : isEditing ? (
            /* Edit/Create Form */
            <form onSubmit={profile ? handleSave : (e) => { e.preventDefault(); handleCreateNew(); }} className="space-y-5">
              {/* Avatar */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-24 h-24 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center">
                    <span className="text-3xl font-semibold text-white">
                      {editForm.firstName.charAt(0)}{editForm.lastName.charAt(0)}
                    </span>
                  </div>
                  <button 
                    type="button"
                    className="absolute bottom-0 right-0 w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-camera-line text-gray-600 text-sm"></i>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={editForm.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={editForm.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                <input
                  type="text"
                  name="address"
                  value={editForm.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">City</label>
                  <input
                    type="text"
                    name="city"
                    value={editForm.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={editForm.zipCode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                <input
                  type="text"
                  name="country"
                  value={editForm.country}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    if (profile) setEditForm(profile);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Saving...
                    </span>
                  ) : profile ? 'Save Changes' : 'Create Profile'}
                </button>
              </div>
            </form>
          ) : activeSection === 'notifications' ? (
            renderNotificationsSection()
          ) : (
            /* View Profile */
            <div>
              {/* Avatar & Name */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-semibold text-white">
                    {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {profile.firstName} {profile.lastName}
                </h3>
                <p className="text-gray-500 text-sm">{profile.email}</p>
              </div>

              {/* Profile Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <i className="ri-phone-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{profile.phone || 'Not provided'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <i className="ri-map-pin-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-sm font-medium text-gray-900">
                      {profile.address ? `${profile.address}, ${profile.city}` : 'Not provided'}
                    </p>
                    {profile.zipCode && (
                      <p className="text-xs text-gray-500">{profile.zipCode}, {profile.country}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-8 space-y-3">
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-edit-line"></i>
                  Edit Profile
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-4 py-3 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-delete-bin-line"></i>
                  Delete Profile
                </button>
              </div>

              {/* Quick Links */}
              <div className="mt-8 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Quick Links</h4>
                <div className="space-y-2">
                  <a href="/shop" className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                    <span className="flex items-center gap-3 text-sm text-gray-700">
                      <i className="ri-shopping-bag-line"></i>
                      Order History
                    </span>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </a>
                  <a href="/shop" className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                    <span className="flex items-center gap-3 text-sm text-gray-700">
                      <i className="ri-heart-line"></i>
                      Wishlist
                    </span>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </a>
                  <a href="/contact" className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                    <span className="flex items-center gap-3 text-sm text-gray-700">
                      <i className="ri-customer-service-line"></i>
                      Support
                    </span>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Profile?</h3>
              <p className="text-gray-500 text-sm mb-6">
                This action cannot be undone. All your profile data will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Deleting...
                    </span>
                  ) : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileSidebar;
